import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" ? window.location.origin : "http://localhost:4000");

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      localStorage.removeItem("authMode");
      window.dispatchEvent(new CustomEvent("hexvault:auth-expired"));
    }
    return Promise.reject(error);
  }
);

export const authService = {
  loginWithPassword: (data) => apiClient.post("/api/auth/login", data),
  syncSession: () => apiClient.post("/api/auth/session"),
  getMe: () => apiClient.get("/api/auth/me"),
};

export const userService = {
  getProfile: () => apiClient.get("/api/users/profile"),
  updateProfile: (data) => apiClient.put("/api/users/profile", data),
  getStorageStats: () => apiClient.get("/api/users/storage-stats"),
  getApiAccess: () => apiClient.get("/api/users/api-access"),
  regenerateApiKey: () => apiClient.post("/api/users/api-access/regenerate"),
};

export const fileService = {
  uploadFile: (file, onProgress) => {
    const formData = new FormData();
    formData.append("file", file);

    return apiClient.post("/api/files/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event) => {
        const total = event.total || 1;
        const percent = Math.round((event.loaded * 100) / total);
        if (onProgress) {
          onProgress({
            loaded: event.loaded || 0,
            total,
            percent,
          });
        }
      },
    });
  },
  listFiles: () => apiClient.get("/api/files"),
  downloadFile: (fileId) =>
    apiClient.get(`/api/files/${fileId}/download`, {
      responseType: "blob",
    }),
  deleteFile: (fileId) => apiClient.delete(`/api/files/${fileId}`),
  createPublicLink: (fileId) => apiClient.post(`/api/files/${fileId}/public-link`),
};

export const adminService = {
  getStorageAccounts: () => apiClient.get("/api/admin/storage-accounts"),
  getStorageAccount: (accountId) => apiClient.get(`/api/admin/storage-accounts/${accountId}`),
  createStorageAccount: (data) => apiClient.post("/api/admin/storage-accounts", data),
  updateStorageAccount: (accountId, data) => apiClient.put(`/api/admin/storage-accounts/${accountId}`, data),
  disableStorageAccount: (accountId) => apiClient.patch(`/api/admin/storage-accounts/${accountId}/disable`),
  enableStorageAccount: (accountId) => apiClient.patch(`/api/admin/storage-accounts/${accountId}/enable`),
  testStorageAccount: (data) => apiClient.post("/api/admin/storage-accounts/test", data),
  getStoragePoolStats: () => apiClient.get("/api/admin/storage-pool"),
  listUsers: () => apiClient.get("/api/admin/users"),
  getUserInsights: () => apiClient.get("/api/admin/users/insights"),
  getSettings: () => apiClient.get("/api/admin/settings"),
  updateSettings: (data) => apiClient.patch("/api/admin/settings", data),
  bulkUpdateUsers: (data) => apiClient.patch("/api/admin/users/bulk", data),
  updateUser: (userId, data) => apiClient.patch(`/api/admin/users/${userId}`, data),
  deleteUser: (userId) => apiClient.delete(`/api/admin/users/${userId}`),
  getActivityLogs: () => apiClient.get("/api/admin/activity-logs"),
  getSystemOverview: () => apiClient.get("/api/admin/system/overview"),
  getSystemLogs: (limit = 60) => apiClient.get(`/api/admin/system/logs?limit=${limit}`),
};

export default apiClient;
